import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { getSession } from '../utils/session'
import { getNotifications as apiGetNotifications, getUnreadCount as apiGetUnreadCount, markAllRead as apiMarkAllRead } from '../utils/api'
import { C, fontStack } from '../constant/theme'

const PANEL_BG = '#0f1923'
const PANEL_BORDER = 'rgba(255,255,255,0.1)'
const TEXT_PRIMARY = 'rgba(255,255,255,0.92)'
const TEXT_SOFT = 'rgba(255,255,255,0.55)'

function notifLabel(n) {
  const actor = n.actorName || 'Someone'
  if (n.type === 'like_media') return `${actor} liked your photo`
  if (n.type === 'comment_media') return `${actor} commented on your photo`
  if (n.type === 'like_comment') return `${actor} liked your comment`
  if (n.type === 'view_media') return `${actor} viewed your photo`
  return `${actor} interacted with your content`
}

function notifIcon(type) {
  if (type === 'like_media' || type === 'like_comment') return '❤️'
  if (type === 'comment_media') return '💬'
  if (type === 'view_media') return '👁'
  return '🔔'
}

function formatTime(ms) {
  try {
    const diff = Date.now() - ms
    if (diff < 60000) return 'just now'
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`
    return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  } catch { return '' }
}

export default function NotificationBell() {
  const session = getSession()
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const wrapperRef = useRef(null)

  // Poll unread count every 30 seconds
  useEffect(() => {
    if (!session) return
    const fetch = () => apiGetUnreadCount().then(({ count }) => setUnreadCount(count || 0)).catch(() => {})
    fetch()
    const timer = setInterval(fetch, 30000)
    return () => clearInterval(timer)
  }, [session])

  // Fetch + mark-all-read when panel opens
  useEffect(() => {
    if (!open) return
    let active = true
    setLoading(true)
    apiGetNotifications()
      .then((notifs) => {
        if (!active) return
        setNotifications(notifs || [])
        setUnreadCount(0)
        apiMarkAllRead().catch(() => {})
      })
      .catch(() => {})
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [open])

  // Close panel on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  if (!session) return null

  const handleNotifClick = (n) => {
    setOpen(false)
    if (n.slug && n.mediaId) navigate(`/g/${n.slug}?media=${n.mediaId}`)
  }

  return (
    <div ref={wrapperRef} style={S.wrapper}>
      <button style={S.bell} onClick={() => setOpen((v) => !v)} title="Notifications" aria-label="Notifications">
        🔔
        {unreadCount > 0 && (
          <span style={S.badge}>{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div style={S.panel}>
          <div style={S.panelHead}>
            <span style={S.panelTitle}>Notifications</span>
          </div>

          <div style={S.panelBody}>
            {loading ? (
              <div style={S.empty}>Loading…</div>
            ) : notifications.length === 0 ? (
              <div style={S.empty}>No notifications yet.</div>
            ) : (
              notifications.map((n) => (
                <div
                  key={String(n._id)}
                  style={{ ...S.notifRow, background: n.read ? 'transparent' : S.unreadBg }}
                  onClick={() => handleNotifClick(n)}
                >
                  <div style={S.notifIconWrap}>
                    <div style={S.notifAvatar}>{(n.actorName || '?').charAt(0).toUpperCase()}</div>
                    <span style={S.notifTypeIcon}>{notifIcon(n.type)}</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.notifText}>{notifLabel(n)}</div>
                    <div style={S.notifTime}>{formatTime(n.createdAt)}</div>
                  </div>
                  {!n.read && <div style={S.unreadDot} />}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const S = {
  wrapper: {
    position: 'relative',
    fontFamily: fontStack,
  },
  bell: {
    position: 'relative',
    width: 40,
    height: 40,
    borderRadius: '50%',
    border: 'none',
    background: C.navy2,
    color: '#fff',
    fontSize: 18,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 12px rgba(0,0,0,.35)',
  },
  badge: {
    position: 'absolute',
    top: -4,
    right: -4,
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    background: '#e0245e',
    color: '#fff',
    fontSize: 10,
    fontWeight: 800,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
    boxSizing: 'border-box',
    border: '2px solid #fff',
  },
  panel: {
    position: 'absolute',
    top: 48,
    right: 0,
    width: 'min(320px, calc(100vw - 32px))',
    maxHeight: 480,
    background: PANEL_BG,
    border: `1px solid ${PANEL_BORDER}`,
    borderRadius: 14,
    boxShadow: '0 8px 40px rgba(0,0,0,.55)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 9001,
  },
  panelHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px 12px',
    borderBottom: `1px solid ${PANEL_BORDER}`,
    flexShrink: 0,
  },
  panelTitle: { fontSize: 15, fontWeight: 800, color: TEXT_PRIMARY },
  panelBody: {
    overflowY: 'auto',
    flex: 1,
  },
  empty: {
    fontSize: 13,
    color: TEXT_SOFT,
    textAlign: 'center',
    padding: '28px 20px',
  },
  notifRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '12px 18px',
    borderBottom: `1px solid ${PANEL_BORDER}`,
    cursor: 'pointer',
  },
  unreadBg: 'rgba(27,53,88,0.45)',
  notifIconWrap: {
    position: 'relative',
    flexShrink: 0,
    width: 38,
    height: 38,
  },
  notifAvatar: {
    width: 38,
    height: 38,
    borderRadius: '50%',
    background: C.navy2,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 15,
    fontWeight: 700,
  },
  notifTypeIcon: {
    position: 'absolute',
    bottom: -3,
    right: -3,
    fontSize: 13,
    lineHeight: 1,
  },
  notifText: {
    fontSize: 13,
    color: TEXT_PRIMARY,
    lineHeight: 1.45,
    wordBreak: 'break-word',
  },
  notifTime: {
    fontSize: 11,
    color: TEXT_SOFT,
    marginTop: 3,
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#1d6fdb',
    flexShrink: 0,
    marginTop: 4,
  },
}
